import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  List,
  MenuList,
  Paper,
  Popper,
  Stack,
  SvgIcon,
  Typography,
  MenuItem as MuiMenuItem,
} from '@mui/material';
import { spaces, palette, borderRadius, customSizes } from '../../../assets/styles/themes/tokens';
import MenuItem from './MenuItem';
import { useTranslation } from 'react-i18next';
import getMuiIcon from '../../../utils/getMuiIcon';
import useRouter from '../../../utils/useRouter';
import { ampliConversionPath, routes } from '../../../constants';
import { useSelector } from 'react-redux';
import { getSubscription, selectUser } from '../../../reducers/user.selectors';
import { SectionHeader } from './SectionHeader';
import { externalLinks } from '../../../constants';
import Logout from '../../Logout';
import { PersonalDetailsSection } from '../../../routes/PersonalDetailsPage/PersonalDetailsPage';
import MuLogo from './MuLogo';
import MenuItemTooltip from './MenuItemTooltip';
import config from 'config';
import { useAmplitudeExperiment } from '../../../utils/ampli';
import { Hint } from '../../Hint';
import { useNewNavigationOnboarding } from '../MasterLayout/NewNavigationOnboardingContext';
import NewNavigationOnboardingHint from '../../NewNavigationOnboardingHint/NewNavigationOnboardingHint';
import useScreenSize from '../../../utils/useScreenSize';
import NewNavigationOnboardingHintMobile from '../../NewNavigationOnboardingHint/NewNavigationOnboardingHintMobile';
import SideNavbarCourseSection from './SideNavbarCourseSection';
import services from '../../../services';

const MOBILE_ONBOARDING_DELAY = 300;

const interactionStyles = {
  '&:hover': {
    backgroundColor: palette.primary.selected,
  },
  '&:focus': {
    backgroundColor: 'transparent',
    outline: 'none',
  },
  '&:focus:hover': {
    backgroundColor: palette.primary.selected,
  },
  '&:active': {
    backgroundColor: palette.common.white,
  }
}; 

const getNavWrapperStyles = (isCollapsed: boolean) => ({
  width: isCollapsed ? customSizes.sideNavbar.collapsedWidth : customSizes.sideNavbar.expandedWidth,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  height: '100%',
  borderRight: `1px solid ${palette.elevation.outlined}`,
  backgroundColor: palette.background.paperElevation2,
});

const getHeaderStyles = (isDrawer: boolean) => ({
  borderBottom: `1px solid ${palette.elevation.outlined}`,
  width: '100%',
  minHeight: isDrawer ? customSizes.sideNavbar.headerHeight.mobile : customSizes.sideNavbar.headerHeight.desktop,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const listStyles = {
  padding: spaces.xs,
  flexGrow: 1,
  width: '100%',
  gap: spaces.xs,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  msOverflowStyle: 'none',  // IE and Edge
  scrollbarWidth: 'none',  // Firefox
};

const closeButtonStyles = {
  borderLeft: `1px solid ${palette.elevation.outlined}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
};

const upgradeButtonWrapperStyles = {
  width: '100%',
  padding: spaces.xs,
  paddingTop: spaces.md,
};

const upgradeButtonCollapsedStyles = {
  width: '100%',
  aspectRatio: 1,
  borderRadius: borderRadius.sm,
  border: `1px solid ${palette.primary.outlinedBorder}`,
};

const footerStyles = {
  paddingX: spaces.md,
  width: '100%',
  borderTop: `1px solid ${palette.elevation.outlined}`,
  height: customSizes.sideNavbar.headerHeight.desktop,
};

const menuCollapsedStyles = {
  borderRadius: borderRadius.md,
  padding: spaces.xs,
  display: 'flex',
  flexDirection: 'column',
};

const popperStyles = {
  position: 'fixed',
  paddingLeft: spaces.xs,
  paddingBottom: spaces.xs,
};

const menuItemCollapsedStyles = {
  cursor: 'pointer',
};

const paragraphIconWrapperStyles = {
  width: customSizes.sideNavbar.collapsedWidth,
  height: customSizes.sideNavbar.collapsedWidth,
  borderTop: `1px solid ${palette.elevation.outlined}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const paragraphIconButtonStyles = {
  width: '100%',
  height: '100%',
  '& svg': {
    color: palette.text.secondary,
  },
  '&:hover': {
    backgroundColor: palette.primary.hover,
  },
};

const linkStyles = {
  textDecoration: 'none',
  color: 'inherit',
};

const menuItemStyles = {
  paddingX: spaces.md,
  paddingY: spaces.xs,
};

interface SideNavbarProps {
  isCollapsed: boolean;
  isDrawer?: boolean;
  onCloseDrawer?: () => void;
}

const SideNavbar: React.FC<SideNavbarProps> = ({
  isCollapsed,
  isDrawer = false,
  onCloseDrawer = () => {},
}) => {
  const { t } = useTranslation();
  const { router: { push } } = useRouter();
  const user = useSelector(selectUser);
  const subscription = useSelector(getSubscription);
  const onboardingStepCounter = useNewNavigationOnboarding();
  const [isMobileOnboardingVisible, setIsMobileOnboardingVisible] = useState(false);

  const anchorEl = React.useRef<HTMLButtonElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

  const {
    variant: lernsetVariant,
    isReady: isClientReady,
  } = useAmplitudeExperiment(user, config.amplitudeExperiments.enableLearnsetsFeature);
  const isLernsetsActive = isClientReady && lernsetVariant === 'on';
  
  const { isSmallScreen, isMediumScreen, isLargeScreen } = useScreenSize();

  const handleLogoClick = () => {
    push(`/${routes.dashboard.root}`);
  };

  const handleUpdatePlanClick = () => {
    services.track.ampliTrackPricingPlansViewedEvent(
      ampliConversionPath.NavigationBar,
      subscription?.status
    );
    push(`/${routes.upgradePackageSelection.root}`);
  };

  const showLernsetsButton = isLernsetsActive && user?.hasDocumentLevelAccess;

  const isDocumentUser = !!(user && user.isDocumentUser);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const navWrapperStyles = useMemo(() => getNavWrapperStyles(isCollapsed), [isCollapsed]);

  const headerStyles = useMemo(() => getHeaderStyles(isDrawer), [isDrawer]);

  const NavLink = ({ to, children }: { to: string, children: React.ReactNode }) => (
    // eslint-disable-next-line react/forbid-dom-props
    <a href={to} target='_blank' rel='noopener noreferrer' style={linkStyles}>
      {children}
    </a>
  );

  useEffect(() => {
    // Delay showing mobile onboarding hints to prevent them from appearing 
    // during drawer transition
    const timeout = setTimeout(() => {
      setIsMobileOnboardingVisible(true);
    }, MOBILE_ONBOARDING_DELAY);

    return () => {
      clearTimeout(timeout);
      setIsMobileOnboardingVisible(false);
    };
  }, []);

  const getUpgradeActionText = () => {
    if (user?.isBasisUser) {
      return t('Upgrade Plan');
    } else if (user?.isDocumentUser || user?.isCourseUser) {
      return t('Update Plan');
    } else if (user?.isKombiUser) {
      return t('For Schools');
    }
    return t('Upgrade Plan');
  };

  return (
    <Box
      component='nav'
      sx={{
        ...navWrapperStyles,
        position: 'relative',
        height: '100%',
      }}
    >
      <Box
        sx={headerStyles}
      >
        <MuLogo variant={isCollapsed ? 'small' : 'full'} onClick={handleLogoClick}
          sx={{
            // eslint-disable-next-line max-len
            minHeight: isDrawer ? customSizes.sideNavbar.headerHeight.mobile : customSizes.sideNavbar.headerHeight.desktop,
            width: '100%',
            paddingLeft: spaces.md,
          }}
        />
        {isDrawer && (
          <Box sx={closeButtonStyles}>
            <IconButton
              onClick={onCloseDrawer}
              data-testid='close-button'
              sx={{
                padding: spaces.md,
                borderRadius: 0,
                ...interactionStyles,
              }}
            >
              <SvgIcon component={getMuiIcon('close')} sx={{ color: palette.text.primary }} />
            </IconButton>
          </Box>
        )}
      </Box>
      <List
        sx={listStyles}
      >
        <Box>
          <Hint
            // eslint-disable-next-line max-len
            open={!onboardingStepCounter.step && onboardingStepCounter.showWalkthrough && !isSmallScreen && !isMediumScreen}
            onClose={onboardingStepCounter.dismissWalkthrough}
            isNew
            title={t('Welcome to the new navigation!')}
            // eslint-disable-next-line max-len
            description={t('You can now find the navigation here. This way you can get to your favorite features faster and easier.')}
            actions={{
              primary: {
                label: t('Make a tour'),
                onClick: () => onboardingStepCounter.startOnboarding(),
              },
              secondary: {
                label: t('Explore later'),
                onClick: () => onboardingStepCounter.dismissWalkthrough(),
              },
            }}
            placement='right-start'
            disablePortal={false}
            offset={{ x: 12 }}
          >
            <Box>
              <MenuItem
                icon='home'
                label='Home'
                to={routes.dashboard.root}
                isCollapsed={isCollapsed}
                isFocused={!onboardingStepCounter?.step && onboardingStepCounter.showWalkthrough}
              />
            </Box>
          </Hint>
        </Box>
        <SectionHeader isCollapsed={isCollapsed} title={t('Library')} />
        <MenuItem icon='searchNormal' label='Search' to={routes.search.root} isCollapsed={isCollapsed} />
        <CollectionsHint
          isMobileOnboardingVisible={isMobileOnboardingVisible}
        >
          <Box>
            <MenuItem
              icon='save'
              label='Saved'
              to={routes.collections.root}
              activePaths={[routes.collections.root, routes.followed.root, routes.pinned.root]}
              isCollapsed={isCollapsed}
              isFocused={
                (onboardingStepCounter?.step === 1 && onboardingStepCounter.showWalkthrough && isLargeScreen) ||
                (onboardingStepCounter?.step === 1 && (isSmallScreen || isMediumScreen))
              }
            />
          </Box>
        </CollectionsHint>
        <DownloadsHint isMobileOnboardingVisible={isMobileOnboardingVisible}>
          <Box>
            <MenuItem
              icon='downloads'
              label='Downloads'
              to={routes.downloads.root}
              isCollapsed={isCollapsed}
              isFocused={
                (onboardingStepCounter?.step === 2 && onboardingStepCounter.showWalkthrough && isLargeScreen) ||
                (onboardingStepCounter?.step === 2 && (isSmallScreen || isMediumScreen))
              }
            />
          </Box>
        </DownloadsHint>
        {showLernsetsButton && <MenuItem
          icon='clipboard'
          label='Lernsets'
          to={routes.lernsetsOverview.root}
          rightSection={
            <Chip component='span' label='Beta' size='small' color='primary' id='lernsets-chip' />
          }
          isCollapsed={isCollapsed}
        />}
        <SideNavbarCourseSection isCollapsed={isCollapsed} />
        <SectionHeader isCollapsed={isCollapsed} title={t('Support')} />
        <MenuItem
          icon='message'
          label='Feedback'
          to={config.newNavigationFeedback.url}
          external
          isCollapsed={isCollapsed}
        />
        <MenuItem
          icon='messageQuestion'
          label='Help'
          to={externalLinks.faq}
          external
          isCollapsed={isCollapsed}
        />
        {isDrawer && (
          <>
            <SectionHeader isCollapsed={isCollapsed} title={t('Settings')} />
            <MenuItem
              icon='profileCircle'
              label='My Profile'
              to={`${routes.personalDetails.root}/${PersonalDetailsSection.PROFILE}`}
              isCollapsed={isCollapsed}
            />
            <MenuItem
              icon='cards'
              label='My Account'
              to={`${routes.personalDetails.root}/${PersonalDetailsSection.ACCOUNT}`}
              isCollapsed={isCollapsed}
            />
            <Logout
              render={(handleLogout) => (
                <MenuItem icon='logout' label='Logout' onClick={handleLogout} isCollapsed={isCollapsed} />
              )}
            />
          </>
        )}
      </List>
      {user?.canUpgrade && (
        <Box
          sx={upgradeButtonWrapperStyles}
        >
          {isCollapsed ? (
            <MenuItemTooltip title={t('Upgrade plan')}>
              <IconButton sx={upgradeButtonCollapsedStyles} onClick={handleUpdatePlanClick}>
                <SvgIcon component={getMuiIcon('arrowUpOutline')} color='primary' />
              </IconButton>
            </MenuItemTooltip>
          ) : (
            <Button
              startIcon={
                <SvgIcon component={getMuiIcon('arrowUpOutline')} />
              }
              variant={isDocumentUser ? 'outlined' : 'contained'}
              size='large'
              color='primary'
              fullWidth
              onClick={handleUpdatePlanClick}
            >
              {getUpgradeActionText()}
            </Button>
          )}
        </Box>
      )}
      {isCollapsed ? (
        <>
          <Popper
            open={isMenuOpen}
            anchorEl={anchorEl.current}
            placement='right'
            sx={popperStyles}
          >
            <Paper
              elevation={8}
              sx={menuCollapsedStyles}
            >
              <MenuList disablePadding>
                <MuiMenuItem
                  sx={menuItemStyles}
                >
                  <NavLink to={externalLinks.imprint}>
                    <Typography variant='body1' sx={menuItemCollapsedStyles}>
                      {t('Imprint')}
                    </Typography>
                  </NavLink>
                </MuiMenuItem>
                <MuiMenuItem
                  sx={menuItemStyles}
                >
                  <NavLink to={externalLinks.dataPolicy}>
                    <Typography variant='body1' sx={menuItemCollapsedStyles}>
                      {t('Privacy')}
                    </Typography>
                  </NavLink>
                </MuiMenuItem>
                <MuiMenuItem
                  sx={menuItemStyles}
                >
                  <NavLink to={externalLinks.tac}>
                    <Typography variant='body1' sx={menuItemCollapsedStyles}>
                      {t('Terms')}
                    </Typography>
                  </NavLink>
                </MuiMenuItem>
              </MenuList>
            </Paper>
          </Popper>
          <MenuItemTooltip title={t('Legal Information')}>
            <Box sx={paragraphIconWrapperStyles}>
              <IconButton
                data-testid='paragraph-icon'
                ref={anchorEl}
                onClick={toggleMenu}
                sx={paragraphIconButtonStyles}
              >
                <SvgIcon component={getMuiIcon('paragraph')} color='secondary' />
              </IconButton>
            </Box>
          </MenuItemTooltip>
        </>
      ) : (
        <Stack
          direction='row'
          sx={footerStyles}
          useFlexGap
          spacing={spaces.md}
          alignItems='center'
        > 
          <NavLink to={externalLinks.imprint}>
            <Typography variant='caption'>
              {t('Imprint')}
            </Typography>
          </NavLink>
          <NavLink to={externalLinks.dataPolicy}>
            <Typography variant='caption'>
              {t('Privacy')}
            </Typography>
          </NavLink>
          <NavLink to={externalLinks.tac}>
            <Typography variant='caption'>
              {t('Terms')}
            </Typography>
          </NavLink>
        </Stack>
      )}
    </Box>
  );
};

export default SideNavbar;

const CollectionsHint = ({
  children,
  isMobileOnboardingVisible,
}: {
  children: JSX.Element,
  isMobileOnboardingVisible: boolean,
}) => {
  const { t } = useTranslation();
  const { isSmallScreen, isMediumScreen } = useScreenSize();
  const onboardingStepCounter = useNewNavigationOnboarding();

  if ((isSmallScreen || isMediumScreen) && !isMobileOnboardingVisible) {
    return children;
  }

  if (isSmallScreen || isMediumScreen) {
    return (
      <NewNavigationOnboardingHintMobile
        // eslint-disable-next-line max-len
        description={t('You can now find in Saved your favorites and collections')}
        step='1/2'
        open={onboardingStepCounter?.step === 1 && onboardingStepCounter.showWalkthrough}
      >
        {children}
      </NewNavigationOnboardingHintMobile>
    );
  } else {
    return (
      <NewNavigationOnboardingHint
        // eslint-disable-next-line max-len
        description={t('You can now find in Saved your favorites and collections')}
        open={onboardingStepCounter?.step === 1 && onboardingStepCounter.showWalkthrough}
        placement='right'
        mobilePlacement='bottom-start'
        buttonText={t('Next')}
        step='1/2'
        disablePortal={false}
      >
        {children}
      </NewNavigationOnboardingHint>
    );
  }
};

const DownloadsHint = ({
  children,
  isMobileOnboardingVisible,
}: {
  children: JSX.Element,
  isMobileOnboardingVisible: boolean,
}) => {
  const { t } = useTranslation();
  const { isSmallScreen, isMediumScreen } = useScreenSize();
  const onboardingStepCounter = useNewNavigationOnboarding();

  if ((isSmallScreen || isMediumScreen) && !isMobileOnboardingVisible) {
    return children;
  }

  if (isSmallScreen || isMediumScreen) {
    return (
      <NewNavigationOnboardingHintMobile
        // eslint-disable-next-line max-len
        description={t('You can find your download history under Downloads.')}
        step='2/2'
        open={onboardingStepCounter?.step === 2 && onboardingStepCounter.showWalkthrough}
      >
        {children}
      </NewNavigationOnboardingHintMobile>
    );
  } else {
    return (
      <NewNavigationOnboardingHint
      // eslint-disable-next-line max-len
        description={t('You can find your download history under Downloads.')}
        open={onboardingStepCounter?.step === 2}
        placement='right'
        buttonText={t('Done')}
        step='2/2'
      >
        {children}
      </NewNavigationOnboardingHint>
    );
  }
};
